import { useState, useEffect } from "react";
import contentstack, { Region } from "@contentstack/delivery-sdk";
import ContentstackLivePreview from "@contentstack/live-preview-utils";

// Live preview is only needed inside the Contentstack editor (preview/dev
// environments). Gate every entry point on GATSBY_CONTENTSTACK_LIVE_PREVIEW so
// init, listeners, and draft fetches never run for public visitors. The flag is
// read INLINE in each guard (not via a shared const) so Gatsby's DefinePlugin
// substitutes a literal that the minifier can fold — on production (flag unset)
// it drops the guarded branch, stripping the preview token from the bundle. A
// shared const is not inlined by the minifier and leaves the token behind. Set
// the flag to "true" only on environments where editors preview drafts.
const apiKey = process.env.GATSBY_CONTENTSTACK_API_KEY;
const environment = process.env.GATSBY_CONTENTSTACK_ENVIRONMENT;
const appHost =
  process.env.GATSBY_CONTENTSTACK_APP_HOST || "gcp-na-app.contentstack.com";

let stack;

export function getStack() {
  if (process.env.GATSBY_CONTENTSTACK_LIVE_PREVIEW !== "true") return null;
  if (stack) return stack;
  const previewToken = process.env.GATSBY_CONTENTSTACK_PREVIEW_TOKEN;
  const previewHost =
    process.env.GATSBY_CONTENTSTACK_LIVE_PREVIEW_HOST ||
    "gcp-na-rest-preview.contentstack.com";
  const regionKey = process.env.GATSBY_CONTENTSTACK_REGION || "GCP_NA";
  stack = contentstack.stack({
    apiKey,
    deliveryToken: "preview-only",
    environment,
    region: Region[regionKey] || Region.GCP_NA,
    branch: "main",
    live_preview: {
      enable: true,
      preview_token: previewToken,
      host: previewHost,
    },
  });
  return stack;
}

let initialized = false;

export function initLivePreview() {
  if (process.env.GATSBY_CONTENTSTACK_LIVE_PREVIEW !== "true") return;
  if (initialized || typeof window === "undefined") return;
  const s = getStack();
  if (!s) return;
  try {
    // Pass the delivery-sdk v5 stack's CONFIG object (s.config) as stackSdk —
    // NOT the stack instance. During init, live-preview-utils runs
    // Config.set("stackSdk", initData.stackSdk) then
    // Config.set("stackSdk.live_preview", ...), and the latter throws
    // "Invalid key: stackSdk.live_preview" unless stackSdk has an own
    // `live_preview` key. The v5 instance keeps that under stack.config, so
    // passing the instance fails; s.config carries
    // live_preview: { preview_token, host, enable }, which satisfies the check
    // and tells the editor we're on the Preview Service (clears the
    // "Preview Service Not Enabled" warning).
    const result = ContentstackLivePreview.init({
      enable: true,
      ssr: false,
      stackSdk: s.config,
      stackDetails: { apiKey, environment, branch: "main" },
      clientUrlParams: { protocol: "https", host: appHost, port: 443 },
    });
    if (result && typeof result.catch === "function") {
      result.catch((err) => console.error("[live-preview] init failed", err));
    }
  } catch (err) {
    console.error("[live-preview] init threw", err);
  }
  initialized = true;
}

export const onEntryChange = (cb) => {
  if (
    process.env.GATSBY_CONTENTSTACK_LIVE_PREVIEW !== "true" ||
    typeof window === "undefined"
  )
    return () => {};
  const uid = ContentstackLivePreview.onEntryChange(cb);
  return () => ContentstackLivePreview.unsubscribeOnEntryChange(uid);
};

// Mirror the build-time createResolvers() in gatsby-node.js: the Delivery SDK
// returns raw JSON RTE, but templates render the resolver-built rendered_body
// HTML. Convert it here so a live entry has the same shape as build-time data.
// The serializer is lazy-imported so it stays out of the production bundle.
async function addRenderedBody(entry, contentTypeUid) {
  if (!entry || (contentTypeUid !== "page" && contentTypeUid !== "news_article"))
    return entry;
  const { jsonToHtml } = await import("@contentstack/json-rte-serializer");
  const toHtml = (doc) =>
    doc && typeof doc === "object" ? jsonToHtml(doc) : doc || null;
  entry.rendered_body = toHtml(entry.body);
  if (Array.isArray(entry.body_sections)) {
    entry.body_sections.forEach((section) => {
      if (section.body_text) {
        section.body_text.rendered_body = toHtml(section.body_text.rich_body);
      }
    });
  }
  return entry;
}

export async function fetchEntry({ contentTypeUid, uid }) {
  if (process.env.GATSBY_CONTENTSTACK_LIVE_PREVIEW !== "true") return null;
  // Only fetch when there's a live-preview hash, i.e. we're inside the
  // Contentstack editor. Outside the editor (normal loads, Lighthouse) there's
  // nothing to preview and the build-time entry is already correct — fetching
  // would hit the Delivery API with the placeholder token and 401.
  const hash = ContentstackLivePreview.hash;
  if (!hash) return null;
  try {
    const s = getStack();
    // Push the current live-preview hash into the stack before fetching so
    // the Delivery API returns the in-editor (draft) version, not published.
    s.livePreviewQuery({ live_preview: hash, contentTypeUid });
    let entry;
    if (uid) {
      // Templates have many entries — fetch the specific one being previewed.
      entry = await s.contentType(contentTypeUid).entry(uid).fetch();
    } else {
      // Singletons (e.g. homepage) — first entry of the type.
      const result = await s.contentType(contentTypeUid).entry().query().find();
      entry = result?.entries?.[0];
    }
    return await addRenderedBody(entry, contentTypeUid);
  } catch (err) {
    console.error(`[live-preview] fetch ${contentTypeUid} failed`, err);
    return null;
  }
}

// React hook: seed from the build-time entry, swap in the live (draft) entry on
// each editor change. Keyed types (page, news_article) accept a live entry only
// when its uid matches the built one, so a stale entry can't leak when Gatsby
// reuses a template across routes. Singletons (homepage) have no uid and always
// take the live entry.
export function useLivePreview(contentTypeUid, built, { singleton = false } = {}) {
  const [live, setLive] = useState(null);
  const uid = built?.uid;
  useEffect(() => {
    if (!singleton && !uid) return undefined;
    return onEntryChange(async () => {
      try {
        const entry = await fetchEntry({ contentTypeUid, uid });
        if (entry) setLive(entry);
      } catch (err) {
        console.error(`[live-preview] ${contentTypeUid} refresh failed`, err);
      }
    });
  }, [contentTypeUid, uid, singleton]);
  if (singleton) return live || built;
  return live && live.uid === uid ? live : built;
}
